import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOnboardingWizard } from "../OnboardingWizardProvider";
import { useAuth } from "../../../lib/AuthContext";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";

export function FormStep() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    stepConfig,
    formData,
    updateFormData,
    goNext,
    goBack,
    isFirstStep,
    markStepComplete,
    currentStep,
    onStepComplete,
    executeAction,
  } = useOnboardingWizard();

  const [localData, setLocalData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill fields from user profile or existing formData
  useEffect(() => {
    const prefilled: Record<string, string> = {};
    stepConfig.fields?.forEach((field) => {
      if (formData[field.name]) {
        prefilled[field.name] = formData[field.name];
      } else if (field.prefillFrom === "email" && user?.email) {
        prefilled[field.name] = user.email;
      }
      // firstName, lastName, phone will be populated from formData if previously entered
    });
    setLocalData(prefilled);
  }, [stepConfig.fields, user, formData]);

  const handleChange = (name: string, value: string) => {
    setLocalData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const isValid = () => {
    return (
      stepConfig.fields?.every((field) => {
        if (!field.required) return true;
        const val = localData[field.name]?.trim();
        if (!val) return false;
        if (
          field.validation?.minLength &&
          val.length < field.validation.minLength
        )
          return false;
        return true;
      }) ?? true
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid()) return;

    setIsSubmitting(true);
    setError(null);

    // Merge local data into wizard form data
    updateFormData(localData);

    // Always signal server to complete the step (with optional action)
    const mergedData = { ...formData, ...localData };
    const action = stepConfig.action || { type: "submitForm" };
    const result = await executeAction(action, {
      formData: mergedData,
    });
    if (!result.success) {
      setError(result.error || t("common.error"));
      setIsSubmitting(false);
      return;
    }
    // Persist action metadata (e.g. created IDs) to formData for page refresh survival
    if (result.meta) {
      updateFormData(result.meta);
    }

    // Mark complete and advance
    markStepComplete(currentStep);
    onStepComplete(currentStep);
    if (!result.serverAdvanced) goNext();
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">
          {t(stepConfig.title)}
        </h2>
        {stepConfig.subtitle && (
          <p className="text-muted-foreground">{t(stepConfig.subtitle)}</p>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {stepConfig.fields?.map((field) => (
          <div key={field.name}>
            <label
              htmlFor={`onboarding-${field.name}`}
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              {t(field.label)}
              {field.required && (
                <span className="text-destructive ml-0.5">*</span>
              )}
            </label>
            {field.type === "select" ? (
              <select
                id={`onboarding-${field.name}`}
                value={localData[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                disabled={field.readOnly}
                className="w-full h-12 px-4 text-base border border-border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50 focus:border-nara-orange disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
              >
                <option value="">{field.placeholder ? t(field.placeholder) : "Select..."}</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                ))}
              </select>
            ) : field.type === "radio" ? (
              <div className="space-y-2 pt-1">
                {field.options?.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${
                      localData[field.name] === opt.value
                        ? "border-nara-orange bg-nara-orange/5"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name={field.name}
                      value={opt.value}
                      checked={localData[field.name] === opt.value}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      className="h-4 w-4 text-nara-orange accent-nara-orange"
                    />
                    <span className="text-sm text-foreground">{t(opt.label)}</span>
                  </label>
                ))}
              </div>
            ) : (
              <input
                id={`onboarding-${field.name}`}
                type={field.type}
                value={localData[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={
                  field.placeholder ? t(field.placeholder) : undefined
                }
                readOnly={field.readOnly}
                disabled={field.readOnly}
                className="w-full h-12 px-4 text-base border border-border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50 focus:border-nara-orange disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
              />
            )}
          </div>
        ))}

        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          {!isFirstStep && stepConfig.backAllowed !== false && (
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-2 px-4 py-3 text-foreground hover:bg-accent rounded-xl transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.back")}
            </button>
          )}
          <button
            type="submit"
            disabled={!isValid() || isSubmitting}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-nara-orange text-nara-charcoal font-semibold rounded-xl hover:bg-nara-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
          >
            {isSubmitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                {t(stepConfig.nextLabel || "common.next")}
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
